import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import { PATHS } from '../../router/paths.ts';
import { useNavigate, useParams } from 'react-router-dom';
import useGetSubscriptionStatus from '@utils/api/organizations/use-get-organization-status.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import { useMemo } from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import PublicLogo from '@components/shared/public-logo.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

const ThankYourRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { organizationId, product } = useParams<{
    organizationId: string;
    product: string;
  }>();

  const { data, isLoading } = useGetSubscriptionStatus(
    Number(organizationId) || 0,
    product || ''
  );

  const statusMessageMap: { [key: string]: string } = {
    OPEN: t(
      'your-account-is-pending-please-check-your-email-for-further-instructions'
    ),
    PAID: t(
      'your-payment-was-successful-you-can-now-use-the-platform-to-create'
    ),
    FAILED: t('account-creation-failed-please-try-again'),
    EXPIRED: t('your-account-has-expired-please-renew-or-contact-support')
  };

  const statusHeadingMap: { [key: string]: string } = {
    OPEN: t('account-pending'),
    PAID: t('thank-you-for-purchasing'),
    FAILED: t('account-creation-failed'),
    EXPIRED: t('account-expired')
  };

  const status = data?.result?.status?.toUpperCase();

  const statusMessage = useMemo(() => {
    return (
      statusMessageMap[status] || t('unknown-status-please-contact-support')
    );
  }, [status, t]);

  const statusHeading = useMemo(() => {
    return statusHeadingMap[status] || t('unknown-status');
  }, [status, t]);

  const handleButtonClick = () => {
    if (data?.status?.toUpperCase() === 'FAILED') {
      navigate(PATHS().REGISTER);
    } else if (data?.status?.toUpperCase() === 'PAID') {
      navigate(PATHS().HOME);
    } else {
      navigate(PATHS().LOGIN);
    }
  };

  const buttonLabel = useMemo(() => {
    return data?.status?.toUpperCase() === 'FAILED'
      ? t('retry')
      : t('go-back-to-workspace');
  }, [data?.status, t]);

  return (
    <div className="bg-app min-h-screen py-8 px-8 lg:px-16 justify-center flex items-center">
      <div className=" px-6 py-24 sm:py-32 lg:px-8  ">
        <div className="mx-auto max-w-2xl text-center border border-hairline bg-app-soft px-12 py-8 rounded-2xl shadow-card">
          <div className="flex flex-col justify-center text-center items-center gap-y-5">
            <PublicLogo className="h-auto w-[11rem]" />
          </div>

          <div className="my-10 gap-y-10">
            <Text
              as="h2"
              size="3xl"
              weight="bold"
              className="mt-2 tracking-tight"
            >
              {statusHeading}
            </Text>
            <Text as="p" size="lg" tone="muted" className="mt-6 leading-8">
              {isLoading ? <Loader height={10} width={3} /> : statusMessage}
            </Text>
            <div className="flex justify-center gap-4 my-6">
              <Button
                label={buttonLabel}
                size={'medium'}
                icon={<ExternalLinkIcon width={16} />}
                variant={'secondary'}
                onClick={handleButtonClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYourRegister;
